import { Select } from "@etudescarre/ui";
import { isNotNil } from "@etudescarre/utils";
import { Funnel as FunnelIcon, X as XIcon } from "@phosphor-icons/react";
import { apiClient } from "@/lib/api/client";
import type { FilterOption } from "./document-table/filter-bar";
import type { FilterPreferencesApi } from "./document-table/use-filter-preferences-sync";

export type BimFilterModel = {
  number: string | null;
  name: string;
  zones: string[];
  performances: string[];
};

export type BimFilterAuthor<M extends BimFilterModel = BimFilterModel> = {
  contractId: number;
  name: string;
  lots: Array<{
    id: number | null;
    number: string | null;
    name: string;
    models: M[];
  }>;
};

type Row = {
  author: BimFilterAuthor;
  lot: BimFilterAuthor["lots"][number];
  model: BimFilterModel;
};

export type BimModelListFilters = {
  lot: string[];
  presta: string[];
  author: string[];
  number: string[];
  name: string[];
  zoneName: string[];
  indexMode: string[];
  hideObsolete: boolean;
  needsUpdate: boolean;
};

type FilterKey = Exclude<
  keyof BimModelListFilters,
  "hideObsolete" | "needsUpdate"
>;
type RowFilterKey = Exclude<FilterKey, "indexMode">;

export const EMPTY_BIM_MODEL_FILTERS: BimModelListFilters = {
  lot: [],
  presta: [],
  author: [],
  number: [],
  name: [],
  zoneName: [],
  indexMode: [],
  hideObsolete: false,
  needsUpdate: false,
};

export const bimModelFilterPreferences: FilterPreferencesApi<BimModelListFilters> =
  {
    load: async (yardId) =>
      (
        await apiClient.GET(
          "/api/yards/{yardId}/bim-models/filter-preferences",
          { params: { path: { yardId } } },
        )
      ).data?.data.filters,
    save: (yardId, filters) =>
      apiClient.PUT("/api/yards/{yardId}/bim-models/filter-preferences", {
        params: { path: { yardId } },
        body: filters,
      }),
  };

const rowMatchers: Record<
  RowFilterKey,
  (row: Row, values: string[]) => boolean
> = {
  author: ({ author }, values) => values.includes(String(author.contractId)),
  lot: ({ lot }, values) => isNotNil(lot.id) && values.includes(String(lot.id)),
  presta: ({ model }, values) =>
    model.performances.some((performance) => values.includes(performance)),
  number: ({ model }, values) =>
    isNotNil(model.number) && values.includes(model.number),
  name: ({ model }, values) => values.includes(model.name),
  zoneName: ({ model }, values) =>
    model.zones.some((zone) => values.includes(zone)),
};

const ROW_FILTER_KEYS: RowFilterKey[] = [
  "author",
  "lot",
  "presta",
  "number",
  "name",
  "zoneName",
];

// Each select only offers values consistent with the other selects of its
// block, plus the origin block for the characteristics block. Lots ignore the
// prestations filter, which selecting a lot fills in.
const OPTION_SCOPES: Record<RowFilterKey, RowFilterKey[]> = {
  author: ["lot", "presta"],
  lot: ["author"],
  presta: ["author", "lot"],
  number: ["author", "lot", "presta", "name", "zoneName"],
  name: ["author", "lot", "presta", "number", "zoneName"],
  zoneName: ["author", "lot", "presta", "number", "name"],
};

export function filterBimModelTree<M extends BimFilterModel>(
  authors: BimFilterAuthor<M>[],
  filters: BimModelListFilters,
  keys: RowFilterKey[] = ROW_FILTER_KEYS,
): BimFilterAuthor<M>[] {
  const active = keys.filter((key) => filters[key].length > 0);
  return authors
    .map((author) => ({
      ...author,
      lots: author.lots
        .map((lot) => ({
          ...lot,
          models: lot.models.filter((model) =>
            active.every((key) =>
              rowMatchers[key]({ author, lot, model }, filters[key]),
            ),
          ),
        }))
        .filter((lot) => lot.models.length > 0),
    }))
    .filter((author) => author.lots.length > 0);
}

function rows(authors: BimFilterAuthor[]): Row[] {
  return authors.flatMap((author) =>
    author.lots.flatMap((lot) =>
      lot.models.map((model) => ({ author, lot, model })),
    ),
  );
}

const compare = (a: string, b: string) =>
  a.localeCompare(b, "fr", { numeric: true });

function uniqueOptions(entries: Array<[string, string]>): FilterOption[] {
  return Array.from(new Map(entries), ([value, label]) => ({
    value,
    label,
  })).sort((a, b) => compare(a.label, b.label));
}

export function buildBimModelFilterOptions(
  authors: BimFilterAuthor[],
  filters: BimModelListFilters,
): Record<RowFilterKey, FilterOption[]> {
  const scoped = (key: RowFilterKey) =>
    rows(filterBimModelTree(authors, filters, OPTION_SCOPES[key]));

  return {
    author: uniqueOptions(
      scoped("author").map(({ author }) => [
        String(author.contractId),
        author.name,
      ]),
    ),
    lot: uniqueOptions(
      scoped("lot").flatMap(
        ({ lot }): Array<[string, string]> =>
          isNotNil(lot.id)
            ? [
                [
                  String(lot.id),
                  isNotNil(lot.number)
                    ? `${lot.number} - ${lot.name}`
                    : lot.name,
                ],
              ]
            : [],
      ),
    ),
    presta: uniqueOptions(
      scoped("presta").flatMap(({ model }) =>
        model.performances.map((name): [string, string] => [name, name]),
      ),
    ),
    number: uniqueOptions(
      scoped("number").flatMap(
        ({ model }): Array<[string, string]> =>
          isNotNil(model.number)
            ? [[model.number, `${model.number} - ${model.name}`]]
            : [],
      ),
    ),
    name: uniqueOptions(
      scoped("name").map(({ model }) => [model.name, model.name]),
    ),
    zoneName: uniqueOptions(
      scoped("zoneName").flatMap(({ model }) =>
        model.zones.map((zone): [string, string] => [zone, zone]),
      ),
    ),
  };
}

export function changeBimModelFilter(
  authors: BimFilterAuthor[],
  filters: BimModelListFilters,
  key: FilterKey,
  values: string[],
): BimModelListFilters {
  const next = { ...filters, [key]: values };
  if (key === "lot") {
    next.presta = Array.from(
      new Set(
        rows(authors)
          .filter((row) => rowMatchers.lot(row, values))
          .flatMap(({ model }) => model.performances),
      ),
    );
  }
  if (key === "lot" || key === "presta" || key === "author") {
    return { ...next, number: [], name: [], zoneName: [], indexMode: [] };
  }
  return next;
}

export function readBimModelFilters(
  searchParams: URLSearchParams,
): BimModelListFilters {
  return {
    lot: searchParams.getAll("lot"),
    presta: searchParams.getAll("presta"),
    author: searchParams.getAll("author"),
    number: searchParams.getAll("number"),
    name: searchParams.getAll("name"),
    zoneName: searchParams.getAll("zoneName"),
    indexMode: searchParams.getAll("indexMode"),
    hideObsolete: searchParams.get("hideObsolete") === "1",
    needsUpdate: searchParams.get("needsUpdate") === "1",
  };
}

export function bimModelFiltersToSearchParams({
  hideObsolete,
  needsUpdate,
  ...lists
}: BimModelListFilters): URLSearchParams {
  const params = new URLSearchParams(
    Object.entries(lists).flatMap(([key, values]) =>
      values.map((value) => [key, value]),
    ),
  );
  if (hideObsolete) params.set("hideObsolete", "1");
  if (needsUpdate) params.set("needsUpdate", "1");
  return params;
}

export function hasActiveBimModelFilters({
  hideObsolete,
  needsUpdate,
  ...lists
}: BimModelListFilters): boolean {
  return (
    hideObsolete ||
    needsUpdate ||
    Object.values(lists).some((values) => values.length > 0)
  );
}

export type BimModelFilterBarProps = {
  authors: BimFilterAuthor[];
  filters: BimModelListFilters;
  canFilterCoordination: boolean;
  onChange: (filters: BimModelListFilters) => void;
};

export function BimModelFilterBar({
  authors,
  filters,
  canFilterCoordination,
  onChange,
}: BimModelFilterBarProps) {
  const options = buildBimModelFilterOptions(authors, filters);
  const indexModeOptions: FilterOption[] = [
    { label: "Indices récents uniquement", value: "recent3" },
  ];
  if (canFilterCoordination) {
    indexModeOptions.push({
      label: "Remonter à la dernière diffusion pour coordination",
      value: "coordination",
    });
  }
  const update =
    (key: FilterKey) =>
    ({ value }: { value: string[] }) =>
      onChange(changeBimModelFilter(authors, filters, key, value));

  return (
    <div className="flex shrink-0 items-start gap-3 border-b border-zinc-200 px-4 py-3 dark:border-zinc-700">
      <FunnelIcon size={18} className="mt-2.5 shrink-0 text-zinc-500" />

      <div className="flex min-w-0 flex-1 flex-wrap items-start gap-3">
        <fieldset className="rounded border border-zinc-200 px-3 py-2 dark:border-zinc-700">
          <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
            Filtres sur origine maquette
          </legend>
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-44">
              <Select
                options={options.lot}
                value={filters.lot}
                onValueChange={update("lot")}
                placeholder="Lots"
                multiple
              />
            </div>
            <div className="w-44">
              <Select
                options={options.presta}
                value={filters.presta}
                onValueChange={update("presta")}
                placeholder="Prestations"
                multiple
              />
            </div>
            <div className="w-44">
              <Select
                options={options.author}
                value={filters.author}
                onValueChange={update("author")}
                placeholder="Auteurs"
                multiple
              />
            </div>
          </div>
        </fieldset>

        <fieldset className="rounded border border-zinc-200 px-3 py-2 dark:border-zinc-700">
          <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
            Filtres sur caractéristiques maquette
          </legend>
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-52">
              <Select
                options={options.number}
                value={filters.number}
                onValueChange={update("number")}
                placeholder="N°"
                multiple
                searchable
              />
            </div>
            <div className="w-64">
              <Select
                options={indexModeOptions}
                value={filters.indexMode}
                onValueChange={update("indexMode")}
                placeholder="Indices"
              />
            </div>
            <div className="w-44">
              <Select
                options={options.name}
                value={filters.name}
                onValueChange={update("name")}
                placeholder="Intitulés"
                multiple
              />
            </div>
            <div className="w-44">
              <Select
                options={options.zoneName}
                value={filters.zoneName}
                onValueChange={update("zoneName")}
                placeholder="Niveaux/Façades"
                multiple
              />
            </div>
            <label className="flex cursor-pointer items-center gap-2 whitespace-nowrap text-sm text-zinc-700 dark:text-zinc-300">
              <input
                type="checkbox"
                checked={filters.hideObsolete}
                onChange={() =>
                  onChange({ ...filters, hideObsolete: !filters.hideObsolete })
                }
                className="h-4 w-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
              />
              Masquer les obsolètes
            </label>
            <label className="flex cursor-pointer items-center gap-2 whitespace-nowrap text-sm text-zinc-700 dark:text-zinc-300">
              <input
                type="checkbox"
                checked={filters.needsUpdate}
                onChange={() =>
                  onChange({ ...filters, needsUpdate: !filters.needsUpdate })
                }
                className="h-4 w-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
              />
              Maquettes à mettre à jour uniquement
            </label>
          </div>
        </fieldset>
      </div>

      {hasActiveBimModelFilters(filters) && (
        <button
          type="button"
          onClick={() => onChange(EMPTY_BIM_MODEL_FILTERS)}
          className="mt-2.5 flex items-center gap-1 rounded px-2 py-1 text-xs text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
        >
          <XIcon size={14} />
          Effacer
        </button>
      )}
    </div>
  );
}
